/**
 * AWENI Module Registry
 *
 * Central registry of all available modules with i18n labels,
 * icons, loading strategies, and navigation placement.
 */

import type { ModuleDescriptor, Lang } from '@/types';

// ---------- Inline SVG Icons ----------

const ICONS = {
  home: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>`,

  games: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6" width="20" height="12" rx="2"/><line x1="6" y1="12" x2="10" y2="12"/><line x1="8" y1="10" x2="8" y2="14"/><circle cx="15" cy="10" r="1"/><circle cx="18" cy="13" r="1"/></svg>`,

  memoriz: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7z"/><path d="M9.5 8.5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5c0 2-2.5 4-2.5 4S9.5 10.5 9.5 8.5z"/></svg>`,

  balancetab: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 10h20"/><path d="M6 16h4"/><path d="M14 16h4"/></svg>`,

  selecto: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>`,

  blog: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/><line x1="8" y1="7" x2="16" y2="7"/><line x1="8" y1="11" x2="14" y2="11"/></svg>`,

  chat: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><path d="M8 10h.01"/><path d="M12 10h.01"/><path d="M16 10h.01"/></svg>`,

  community: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`,

  settings: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>`,

  family: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/><line x1="12" y1="17" x2="12" y2="20"/><line x1="10.5" y1="18.5" x2="13.5" y2="18.5"/></svg>`,

  watch: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>`,
} as const;

// ---------- Module Registry ----------

export const MODULE_REGISTRY: ModuleDescriptor[] = [
  {
    id: 'home',
    label: {
      en: 'Home',
      fr: 'Accueil',
      ar: '\u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629',
      es: 'Inicio',
      de: 'Startseite',
    },
    icon: ICONS.home,
    loadStrategy: 'import',
    entryPoint: '@modules/home/HomeModule',
    apiBase: '/aweni/api/v1/briefing',
    offlineCapable: true,
    requiredPlan: 'free',
    preloadData: ['briefing', 'activity-feed'],
    cacheStrategy: 'stale-while-revalidate',
    navPosition: 0,
    color: '#10B981',
  },
  {
    id: 'games',
    label: {
      en: 'Games',
      fr: 'Jeux',
      ar: '\u0623\u0644\u0639\u0627\u0628',
      es: 'Juegos',
      de: 'Spiele',
    },
    icon: ICONS.games,
    loadStrategy: 'iframe',
    entryPoint: '/Projects/Games/hub/',
    apiBase: '/aweni/api/v1/games',
    offlineCapable: true,
    requiredPlan: 'free',
    preloadData: ['game-catalog'],
    cacheStrategy: 'cache-first',
    navPosition: 1,
    color: '#3B82F6',
  },
  {
    id: 'memoriz',
    label: {
      en: 'Quran',
      fr: 'Coran',
      ar: '\u0627\u0644\u0642\u0631\u0622\u0646',
      es: 'Cor\u00e1n',
      de: 'Quran',
    },
    icon: ICONS.memoriz,
    // 2026-04-30: switched from `import` (custom AWENI Memoriz module) to
    // `iframe` pointing at the canonical Memoriz app. Per CLAUDE.md "No
    // redundancy between AWENI apps \u2014 each feature lives in ONE canonical
    // app only." Memoriz at /memoriz/ already covers Quran + Prayer Times +
    // Adhkar + Verse of Day + Stats; AWENI's stub module duplicated only
    // a fraction of that. The standalone Memoriz app is now the single
    // source of truth; AWENI shows it as an embedded experience and only
    // adds shell chrome (top bar, language, theme) around it.
    loadStrategy: 'iframe',
    entryPoint: '/memoriz/',
    apiBase: '/aweni/api/v1/memoriz',
    offlineCapable: true,
    requiredPlan: 'free',
    cacheStrategy: 'network-first',
    navPosition: 2,
    color: '#F59E0B',
  },
  {
    id: 'balancetab',
    label: {
      en: 'Money',
      fr: 'Argent',
      ar: '\u0627\u0644\u0645\u0627\u0644',
      es: 'Dinero',
      de: 'Geld',
    },
    icon: ICONS.balancetab,
    // 2026-04-30: switched from `import` to `iframe` pointing at the
    // canonical BalanceTab app. Same rationale as Memoriz \u2014 BT 2.0's
    // polymorphic ledger (currency / hours / days / points) is too
    // complex to maintain in two codebases. /balancetab/ is the single
    // source of truth.
    loadStrategy: 'iframe',
    entryPoint: '/balancetab/',
    apiBase: '/aweni/api/v1/balancetab',
    offlineCapable: true,
    requiredPlan: 'free',
    cacheStrategy: 'network-first',
    navPosition: 3,
    color: '#8B5CF6',
  },
  {
    id: 'selecto',
    label: {
      en: 'Order',
      fr: 'Commander',
      ar: '\u0627\u0644\u0637\u0644\u0628',
      es: 'Pedir',
      de: 'Bestellen',
    },
    icon: ICONS.selecto,
    loadStrategy: 'iframe',
    entryPoint: '/Projects/SelectO/frontend/',
    apiBase: '/aweni/api/v1/selecto',
    offlineCapable: false,
    requiredPlan: 'free',
    cacheStrategy: 'network-first',
    color: '#F97316',
  },
  {
    id: 'blog',
    label: {
      en: 'Read',
      fr: 'Lire',
      ar: '\u0627\u0644\u0642\u0631\u0627\u0621\u0629',
      es: 'Leer',
      de: 'Lesen',
    },
    icon: ICONS.blog,
    // 2026-04-30: switched from `import` (AWENI's custom blog reader) to
    // `iframe` pointing at /blog/ \u2014 the standalone blog with 335 published
    // articles. SEO strongly prefers a single canonical URL per article,
    // and the blog's navigation/comments/pagination are already polished.
    loadStrategy: 'iframe',
    entryPoint: '/blog/',
    apiBase: '/aweni/api/v1/blog',
    offlineCapable: true,
    requiredPlan: 'free',
    cacheStrategy: 'stale-while-revalidate',
    color: '#14B8A6',
  },
  {
    id: 'chat',
    label: {
      en: 'Chat',
      fr: 'Chat',
      ar: '\u0627\u0644\u0645\u062D\u0627\u062F\u062B\u0629',
      es: 'Chat',
      de: 'Chat',
    },
    icon: ICONS.chat,
    loadStrategy: 'import',
    entryPoint: '@modules/chat/ChatModule',
    apiBase: '/aweni/api/v1/chat',
    offlineCapable: false,
    requiredPlan: 'free',
    cacheStrategy: 'network-first',
    color: '#10B981',
  },
  {
    id: 'community',
    label: {
      en: 'Community',
      fr: 'Communaut\u00e9',
      ar: '\u0627\u0644\u0645\u062C\u062A\u0645\u0639',
      es: 'Comunidad',
      de: 'Gemeinschaft',
    },
    icon: ICONS.community,
    loadStrategy: 'import',
    entryPoint: '@modules/community/CommunityModule',
    apiBase: '/aweni/api/v1/community',
    offlineCapable: false,
    requiredPlan: 'free',
    preloadData: ['community-groups'],
    cacheStrategy: 'network-first',
    color: '#6366F1',
  },
  {
    id: 'parental',
    label: {
      en: 'Parental Controls',
      fr: 'Contr\u00f4le parental',
      ar: '\u0627\u0644\u0631\u0642\u0627\u0628\u0629 \u0627\u0644\u0623\u0628\u0648\u064A\u0629',
      es: 'Control parental',
      de: 'Kindersicherung',
    },
    icon: ICONS.settings,
    loadStrategy: 'import',
    entryPoint: '@modules/parental/ParentalDashboard',
    apiBase: '/aweni/api/v1/preferences',
    offlineCapable: true,
    requiredPlan: 'family',
    cacheStrategy: 'network-first',
    color: '#8B5CF6',
  },
  {
    id: 'upgrade',
    label: {
      en: 'Upgrade',
      fr: 'Passer au premium',
      ar: '\u0627\u0644\u062A\u0631\u0642\u064A\u0629',
      es: 'Mejorar plan',
      de: 'Upgrade',
    },
    icon: ICONS.settings,
    loadStrategy: 'import',
    entryPoint: '@modules/upgrade/UpgradeModule',
    apiBase: '/aweni/api/v1/preferences',
    offlineCapable: false,
    requiredPlan: 'free',
    cacheStrategy: 'network-first',
    color: '#3B82F6',
  },
  {
    id: 'family',
    label: {
      en: 'Family',
      fr: 'Famille',
      ar: '\u0627\u0644\u0639\u0627\u0626\u0644\u0629',
      es: 'Familia',
      de: 'Familie',
    },
    icon: ICONS.family,
    loadStrategy: 'import',
    entryPoint: '@modules/family/FamilyModule',
    apiBase: '/aweni/api/v1/family',
    offlineCapable: false,
    requiredPlan: 'family',
    cacheStrategy: 'network-first',
    color: '#EC4899',
  },
  {
    id: 'watch',
    label: {
      en: 'Watch',
      fr: 'Regarder',
      ar: '\u0634\u0627\u0647\u062F',
      es: 'Ver',
      de: 'Ansehen',
    },
    icon: ICONS.watch,
    loadStrategy: 'import',
    entryPoint: '@modules/watch/WatchModule',
    apiBase: '/aweni/api/v1/watch',
    offlineCapable: true,
    requiredPlan: 'free',
    preloadData: ['content-catalog'],
    cacheStrategy: 'cache-first',
    color: '#EF4444',
  },
  {
    id: 'auth',
    label: {
      en: 'Account',
      fr: 'Compte',
      ar: '\u0627\u0644\u062D\u0633\u0627\u0628',
      es: 'Cuenta',
      de: 'Konto',
    },
    icon: ICONS.settings,
    loadStrategy: 'import',
    entryPoint: '@modules/auth/AuthModule',
    apiBase: '/identity',
    offlineCapable: false,
    requiredPlan: 'free',
    cacheStrategy: 'network-first',
    color: '#10B981',
    // No navPosition — auth is never in the bottom nav
  },
  {
    id: 'profile',
    label: {
      en: 'Profile',
      fr: 'Profil',
      ar: '\u0627\u0644\u0645\u0644\u0641 \u0627\u0644\u0634\u062E\u0635\u064A',
      es: 'Perfil',
      de: 'Profil',
    },
    icon: ICONS.settings,
    loadStrategy: 'import',
    entryPoint: '@modules/profile/ProfileModule',
    apiBase: '/identity',
    offlineCapable: true,
    requiredPlan: 'free',
    cacheStrategy: 'network-first',
    color: '#6366F1',
    // No navPosition — accessible from TopBar avatar
  },
  {
    id: 'settings',
    label: {
      en: 'Settings',
      fr: 'Param\u00e8tres',
      ar: '\u0627\u0644\u0625\u0639\u062F\u0627\u062F\u0627\u062A',
      es: 'Ajustes',
      de: 'Einstellungen',
    },
    icon: ICONS.settings,
    loadStrategy: 'import',
    entryPoint: '@modules/settings/SettingsModule',
    apiBase: '/aweni/api/v1/preferences',
    offlineCapable: true,
    requiredPlan: 'free',
    cacheStrategy: 'cache-first',
    color: '#6B7280',
  },
];

// ---------- Lookup Helpers ----------

/**
 * Get a module descriptor by its ID.
 */
export function getModule(id: string): ModuleDescriptor | undefined {
  return MODULE_REGISTRY.find((m) => m.id === id);
}

/**
 * Get modules assigned to the bottom navigation bar (navPosition 0-4),
 * sorted by position.
 */
export function getNavModules(): ModuleDescriptor[] {
  return MODULE_REGISTRY
    .filter((m) => m.navPosition !== undefined)
    .sort((a, b) => (a.navPosition ?? 0) - (b.navPosition ?? 0));
}

/**
 * Get modules that appear in the "More" overflow menu
 * (those without a navPosition).
 */
export function getMoreModules(): ModuleDescriptor[] {
  return MODULE_REGISTRY.filter((m) => m.navPosition === undefined);
}

/**
 * Get a localized label for the given module.
 */
export function getModuleLabel(id: string, lang: Lang): string {
  const mod = getModule(id);
  return mod ? mod.label[lang] : id;
}

/**
 * Alias for getModule — used by App.ts.
 */
export function getModuleById(id: string): ModuleDescriptor | undefined {
  return getModule(id);
}

/**
 * Alias for getNavModules — used by App.ts.
 */
export function getBottomNavModules(): ModuleDescriptor[] {
  return getNavModules();
}
